import React, { useState, useEffect } from 'react';
import { fetchAuthenticatedMedia } from '../../services/mediaLibraryService';

/**
 * Imagem que carrega via fetch com autenticação (Bearer token).
 * Necessário quando a URL exige Authorization e <img src> não envia headers.
 */
const MIN_LOADING_MS = 200;

const PLACEHOLDER = {
  dark: {
    wrap: { backgroundColor: '#2a3942' },
    spinnerBorder: '2px solid #8696a0',
    label: { color: '#8696a0', fontSize: 12 },
  },
  bubbleOutgoing: {
    wrap: { backgroundColor: 'rgba(255,255,255,0.5)', minHeight: 120, minWidth: 120 },
    spinnerBorder: '2px solid rgba(17,27,33,0.35)',
    label: { color: '#54656f', fontSize: 12 },
  },
  bubbleIncoming: {
    wrap: { backgroundColor: 'rgba(0,0,0,0.06)', minHeight: 120, minWidth: 120 },
    spinnerBorder: '2px solid rgba(0,0,0,0.25)',
    label: { color: '#667781', fontSize: 12 },
  },
};

const AuthenticatedImage = ({
  imageUrl,
  alt = '',
  style = {},
  onClick,
  className,
  placeholderVariant = 'dark',
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!imageUrl) {
      setLoading(true);
      setError(false);
      setBlobUrl(null);
      return;
    }

    setError(false);
    setLoading(true);

    // Base64, data: ou blob: URL - usar diretamente, sem fetch
    if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
      setBlobUrl(imageUrl);
      setLoading(false);
      return;
    }

    let revoked = false;
    let createdBlobUrl = null;
    const startedAt = Date.now();

    const fetchImage = async () => {
      try {
        const { data, headers } = await fetchAuthenticatedMedia(imageUrl, 'blob');
        if (revoked) return;
        const type = headers?.['content-type'] || 'image/jpeg';
        const url = window.URL.createObjectURL(new Blob([data], { type }));
        createdBlobUrl = url;
        setBlobUrl(url);
      } catch (err) {
        if (!revoked) {
          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
          if (remaining > 0) {
            await new Promise((r) => setTimeout(r, remaining));
          }
          if (!revoked) setError(true);
        }
      } finally {
        if (!revoked) setLoading(false);
      }
    };

    fetchImage();

    return () => {
      revoked = true;
      if (createdBlobUrl && createdBlobUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(createdBlobUrl);
      }
    };
  }, [imageUrl]);

  const ph = PLACEHOLDER[placeholderVariant] || PLACEHOLDER.dark;

  // Loading: sem URL ainda ou fetch em andamento
  if (loading || (!blobUrl && !error && !imageUrl)) {
    return (
      <div
        style={{
          minWidth: 100,
          minHeight: 100,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          ...ph.wrap,
          ...style,
        }}
      >
        <style>{`@keyframes authenticated-image-spin { to { transform: rotate(360deg); } }`}</style>
        <div
          style={{
            width: 24,
            height: 24,
            border: ph.spinnerBorder,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            boxSizing: 'border-box',
            animation: 'authenticated-image-spin 0.8s linear infinite',
          }}
        />
        <span style={ph.label}>Carregando...</span>
      </div>
    );
  }

  // Erro só quando o carregamento falhou de fato
  if (error) {
    return (
      <div
        style={{
          minWidth: 100,
          minHeight: 80,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...ph.wrap,
          ...style,
        }}
      >
        <span style={ph.label}>Erro ao carregar imagem</span>
      </div>
    );
  }

  if (!blobUrl) {
    return null;
  }

  return <img src={blobUrl} alt={alt} style={style} onClick={onClick} className={className} />;
};

export default AuthenticatedImage;
